import React, { useState, useEffect, useRef } from 'react';
import { 
  Church, 
  Users, 
  Baby, 
  Heart, 
  Droplets, 
  HandHeart, 
  Sparkles,
  UserPlus,
  Globe,
  MapPin
} from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const AttendanceSection = ({ attendanceData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('Gesamt');
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  if (!attendanceData) return null;

  const { total, locations } = attendanceData;

  // Welche Daten zeigen?
  const activeData = selectedLocation === 'Gesamt' 
    ? total 
    : locations.find(loc => loc.name === selectedLocation);

  if (!activeData) return null;

  // Die wichtigsten Highlights für den großen Hero-Bereich
  const heroMetrics = [
    {
      key: 'total_attendees',
      value: activeData.total_attendees,
      label: 'Teilnehmer',
      sublabel: 'haben mit uns Gottesdienst gefeiert',
      icon: Users,
      size: 'xl'
    },
    {
      key: 'services',
      value: activeData.services,
      label: 'Gottesdienste',
      sublabel: 'haben wir gefeiert',
      icon: Church,
      size: 'md'
    },
    {
      key: 'avg_per_service',
      value: activeData.avg_per_service,
      label: 'Schnitt',
      sublabel: 'pro Gottesdienst',
      icon: Sparkles,
      size: 'md'
    }
  ];

  // Detail-Kennzahlen
  const detailMetrics = [
    { 
      value: activeData.kids, 
      label: 'Kids', 
      sublabel: 'in der KidsChurch',
      icon: Baby,
      color: 'orange'
    },
    { 
      value: activeData.first_time_visitors, 
      label: 'Erstbesucher', 
      sublabel: 'haben uns besucht',
      icon: UserPlus,
      color: 'white'
    },
    { 
      value: activeData.decisions_for_jesus, 
      label: 'Entscheidungen', 
      sublabel: 'für Jesus',
      icon: Heart,
      color: 'orange'
    },
    { 
      value: activeData.baptisms, 
      label: 'Taufen', 
      sublabel: 'haben wir gefeiert',
      icon: Droplets,
      color: 'white'
    },
    { 
      value: activeData.team, 
      label: 'Team', 
      sublabel: 'hat sonntags gedient',
      icon: HandHeart,
      color: 'orange'
    }
  ];

  // Online & Ministry nur wenn Daten vorhanden
  if (activeData.online > 0) {
    detailMetrics.push({
      value: activeData.online,
      label: 'Online',
      sublabel: 'haben mitgefeiert',
      icon: Globe,
      color: 'white'
    });
  }

  if (activeData.ministry_prayer > 0) {
    detailMetrics.push({
      value: activeData.ministry_prayer,
      label: 'Gebete',
      sublabel: 'durchs Ministry-Team',
      icon: Sparkles,
      color: 'orange'
    });
  }

  const locationOptions = ['Gesamt', ...locations.map(l => l.name)];

  return (
    <section 
      ref={sectionRef} 
      id="attendance" 
      data-testid="attendance-section"
      className="py-24 px-6 bg-black border-t border-white/10 relative overflow-hidden"
    >
      {/* Background accent */}
      <div className="absolute top-0 left-0 w-1/2 h-full opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_left,rgba(255,70,0,0.5),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#FF4600]"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
              Unsere Zahlen {attendanceData.year}
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-none">
            Was wir<br />
            <span className="text-[#FF4600]">gefeiert</span> haben
          </h2>
        </div>

        {/* Location Filter */}
        <div className="mb-12 flex flex-wrap gap-2">
          {locationOptions.map((location) => (
            <button
              key={location}
              data-testid={`location-filter-${location.toLowerCase()}`}
              onClick={() => setSelectedLocation(location)}
              className={`px-5 py-3 font-bold uppercase tracking-widest text-sm transition-all flex items-center gap-2 ${
                selectedLocation === location
                  ? 'bg-[#FF4600] text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              {location !== 'Gesamt' && <MapPin className="w-4 h-4" strokeWidth={2.5} />}
              {location}
            </button>
          ))}
        </div>

        {/* Hero Metric - Big Number */}
        <div className="mb-8">
          <div 
            data-testid="hero-metric-attendees"
            className={`bg-gradient-to-br from-[#FF4600] to-[#ff6b2c] p-8 md:p-16 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            key={`hero-${selectedLocation}`}
          >
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <p className="text-white/80 uppercase tracking-widest font-bold text-sm mb-4">
                  {heroMetrics[0].sublabel}
                </p>
                <p className="text-7xl md:text-9xl font-black text-white leading-none">
                  <AnimatedCounter 
                    key={`${selectedLocation}-attendees`}
                    end={heroMetrics[0].value} 
                    duration={2500} 
                  />
                </p>
                <p className="text-white text-2xl md:text-3xl font-black uppercase tracking-tight mt-2">
                  {heroMetrics[0].label}
                </p>
              </div>
              <Users className="w-24 h-24 md:w-32 md:h-32 text-white/20 flex-shrink-0" strokeWidth={1} />
            </div>
          </div>
        </div>

        {/* Secondary Hero Row */}
        <div className="grid md:grid-cols-2 gap-4 mb-12">
          {heroMetrics.slice(1).map((metric, idx) => {
            const Icon = metric.icon;
            return (
              <div
                key={`${selectedLocation}-${metric.key}`}
                data-testid={`hero-metric-${metric.key}`}
                className={`bg-white/5 border border-white/10 p-8 transition-all duration-700 hover:border-[#FF4600]/50 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${(idx + 1) * 100}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className="w-10 h-10 text-[#FF4600]" strokeWidth={2} />
                  <span className="text-xs font-bold uppercase tracking-widest text-white/40">
                    {metric.sublabel}
                  </span>
                </div>
                <p className="text-5xl md:text-6xl font-black text-white">
                  <AnimatedCounter 
                    key={`${selectedLocation}-${metric.key}`}
                    end={metric.value} 
                    duration={2000}
                  />
                </p>
                <p className="text-[#FF4600] font-black uppercase tracking-widest text-sm mt-2">
                  {metric.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Detail Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {detailMetrics.map((metric, idx) => {
            const Icon = metric.icon;
            const isOrange = metric.color === 'orange';
            return (
              <div
                key={`${selectedLocation}-detail-${idx}`}
                data-testid={`detail-metric-${metric.label.toLowerCase()}`}
                className={`p-6 border transition-all duration-700 hover:scale-105 ${
                  isOrange
                    ? 'bg-[#FF4600]/10 border-[#FF4600]/30 hover:bg-[#FF4600]/20 hover:border-[#FF4600]'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/30'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${idx * 80 + 400}ms` }}
              >
                <Icon 
                  className={`w-8 h-8 mb-4 ${isOrange ? 'text-[#FF4600]' : 'text-white'}`} 
                  strokeWidth={2} 
                />
                <p className="text-3xl md:text-4xl font-black text-white">
                  <AnimatedCounter 
                    key={`${selectedLocation}-${idx}`}
                    end={metric.value} 
                    duration={1800} 
                  />
                </p>
                <p className={`text-xs font-bold uppercase tracking-widest mt-2 ${
                  isOrange ? 'text-[#FF4600]' : 'text-white/80'
                }`}>
                  {metric.label}
                </p>
                <p className="text-xs text-white/40 mt-1 font-light">
                  {metric.sublabel}
                </p>
              </div>
            );
          })}
        </div>

        {/* Community Numbers - only shown for "Gesamt" */}
        {selectedLocation === 'Gesamt' && (
          <div 
            className={`mt-12 grid md:grid-cols-2 gap-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '1000ms' }}
          >
            <div 
              data-testid="community-fixed-team"
              className="bg-white/5 border border-white/10 p-8 hover:border-[#FF4600]/50 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <HandHeart className="w-10 h-10 text-[#FF4600]" strokeWidth={2} />
                <span className="text-xs font-bold uppercase tracking-widest text-white/40">
                  Feste Mitarbeiter
                </span>
              </div>
              <p className="text-5xl font-black text-white">
                <AnimatedCounter end={total.fixed_team_members} duration={2000} />
              </p>
              <p className="text-[#FF4600] font-black uppercase tracking-widest text-sm mt-2">
                in unseren Teams
              </p>
            </div>
            
            <div 
              data-testid="community-groups"
              className="bg-white/5 border border-white/10 p-8 hover:border-[#FF4600]/50 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <Users className="w-10 h-10 text-[#FF4600]" strokeWidth={2} />
                <span className="text-xs font-bold uppercase tracking-widest text-white/40">
                  Teilnehmer in Groups
                </span>
              </div>
              <p className="text-5xl font-black text-white">
                <AnimatedCounter end={total.group_participants} duration={2000} />
              </p>
              <p className="text-[#FF4600] font-black uppercase tracking-widest text-sm mt-2">
                in Kleingruppen
              </p>
            </div>
          </div>
        )}

        {/* Thank You Quote */}
        <div className={`mt-16 p-8 md:p-12 border-l-4 border-[#FF4600] bg-gradient-to-r from-[#FF4600]/10 to-transparent transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
        style={{ transitionDelay: '1200ms' }}>
          <p className="text-xs uppercase tracking-widest font-bold text-[#FF4600] mb-3">
            Danke
          </p>
          <p className="text-2xl md:text-4xl font-black text-white leading-tight">
            Jede Zahl ist eine <span className="text-[#FF4600]">Geschichte</span>.<br />
            Jede Geschichte ist ein <span className="text-[#FF4600]">Wunder</span>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AttendanceSection;
